import { useEffect, useRef } from "react"
import type { Suggestion, PreviewRow, ManualAccount, Tab } from "~/react/components/email_accounts/types"

const STORAGE_VERSION = 1

interface ProvisioningStorageData {
  version?: number
  domainsCount: number
  baseDomain: string
  suggestions: Suggestion[]
  selectedMap: Record<string, boolean>
  showManualForm: boolean
  manualDomain: string
  redirectUrl: string
  tab: Tab
  preview?: PreviewRow[]
  showPreview?: boolean
  manualMode?: boolean
  manualAccounts?: ManualAccount[]
}

interface UseProvisioningStorageProps {
  storageKey: string
  domainsCount: number
  baseDomain: string
  suggestions: Suggestion[]
  selectedMap: Record<string, boolean>
  showManualForm: boolean
  manualDomain: string
  redirectUrl: string
  tab: Tab
  preview: PreviewRow[]
  showPreview: boolean
  manualMode: boolean
  manualAccounts: ManualAccount[]
  setDomainsCount: (value: number) => void
  setBaseDomain: (value: string) => void
  setSuggestions: (value: Suggestion[]) => void
  setSelectedMap: (value: Record<string, boolean>) => void
  setShowManualForm: (value: boolean) => void
  setManualDomain: (value: string) => void
  setRedirectUrl: (value: string) => void
  setTab: (value: Tab) => void
  setPreview: (value: PreviewRow[]) => void
  setShowPreview: (value: boolean) => void
  setManualMode: (value: boolean) => void
  setManualAccounts: (value: ManualAccount[]) => void
}

export const useProvisioningStorage = ({
  storageKey,
  domainsCount,
  baseDomain,
  suggestions,
  selectedMap,
  showManualForm,
  manualDomain,
  redirectUrl,
  tab,
  preview,
  showPreview,
  manualMode,
  manualAccounts,
  setDomainsCount,
  setBaseDomain,
  setSuggestions,
  setSelectedMap,
  setShowManualForm,
  setManualDomain,
  setRedirectUrl,
  setTab,
  setPreview,
  setShowPreview,
  setManualMode,
  setManualAccounts,
}: UseProvisioningStorageProps) => {
  const hasRestoredRef = useRef(false)

  useEffect(() => {
    hasRestoredRef.current = false
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const data: ProvisioningStorageData = JSON.parse(saved)

        const dataVersion = data.version ?? 0
        if (dataVersion !== STORAGE_VERSION) {
          localStorage.removeItem(storageKey)
          setTimeout(() => {
            hasRestoredRef.current = true
          }, 0)
          return
        }

        if (data.domainsCount) setDomainsCount(data.domainsCount)
        if (data.baseDomain) setBaseDomain(data.baseDomain)
        if (data.suggestions) {
          const readySuggestions = data.suggestions.filter((s) => s.status === "ready")
          setSuggestions(readySuggestions)
        }
        if (data.selectedMap) setSelectedMap(data.selectedMap)
        if (typeof data.showManualForm === "boolean") setShowManualForm(data.showManualForm)
        if (data.manualDomain) setManualDomain(data.manualDomain)
        if (data.redirectUrl) setRedirectUrl(data.redirectUrl)
        if (data.tab) setTab(data.tab)
        if (Array.isArray(data.preview) && data.preview.length > 0) {
          const readyPreview = data.preview.filter((p) => p.status === "ready")
          if (readyPreview.length > 0) {
            setPreview(readyPreview)
            if (typeof data.showPreview === "boolean" && data.showPreview) {
              setShowPreview(true)
            }
          } else {
            setShowPreview(false)
          }
        } else {
          setShowPreview(false)
        }
        if (typeof data.manualMode === "boolean") setManualMode(data.manualMode)
        if (Array.isArray(data.manualAccounts)) setManualAccounts(data.manualAccounts)
      }
      setTimeout(() => {
        hasRestoredRef.current = true
      }, 0)
    } catch (e) {
      setTimeout(() => {
        hasRestoredRef.current = true
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey])

  useEffect(() => {
    if (!hasRestoredRef.current) return
    try {
      const data: ProvisioningStorageData = {
        version: STORAGE_VERSION,
        domainsCount,
        baseDomain,
        suggestions: suggestions.filter((s) => s.status === "ready"), // Only save ready suggestions
        selectedMap,
        showManualForm,
        manualDomain,
        redirectUrl,
        tab,
        preview,
        showPreview,
        manualMode,
        manualAccounts,
      }
      localStorage.setItem(storageKey, JSON.stringify(data))
    } catch (e) {}
  }, [
    domainsCount,
    baseDomain,
    suggestions,
    selectedMap,
    showManualForm,
    manualDomain,
    redirectUrl,
    tab,
    preview,
    showPreview,
    manualMode,
    manualAccounts,
  ])
}
